import React, { useEffect, useState } from 'react';
import { FaUserAlt, FaEye, FaEyeSlash, FaLock } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import '../styles/LoginForm.css';
import axios from 'axios';
import { GridLoader } from 'react-spinners';
import ReactDOM from 'react-dom';

function LoginForm({ closeLoginModal }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState({ username: '', password: '' });
  const [loading, isLoading] = useState(false);
  const [showDeviceLockInfo, setShowDeviceLockInfo] = useState(false);
  // const [currentDeviceHash, setCurrentDeviceHash] = useState('');
  const [locationModal, setLocationModal] = useState(false);

  const navigate = useNavigate();
  const baseURL = sessionStorage.getItem('baseURL');

  // Replace getDeviceKey with this:
  const getDeviceKey = async () => {

    const hashString = async (str) => {
      const data = new TextEncoder().encode(str);
      const buffer = await crypto.subtle.digest('SHA-256', data);
      return Array.from(new Uint8Array(buffer))
        .map(b => b.toString(16).padStart(2, '0')).join('');
    };

    // ── Coarse geolocation, rounded to ~11km to absorb normal movement ──
    const getCoarseLocation = () => {
      return new Promise((resolve) => {
        if (!navigator.geolocation) return resolve('geo-unavailable');
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const lat = pos.coords.latitude.toFixed(8);
            const lon = pos.coords.longitude.toFixed(8);
            console.log(`Geolocation success: ${lat},${lon}`);
            resolve(`${lat},${lon}`);
          },
          (err) => resolve(`geo-denied-${err.code}`), // 1=denied, 2=unavailable, 3=timeout
          {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 0
          } // cache up to 1hr, 3s timeout
        );
      });
    };

    const geo = await getCoarseLocation();
    if (!geo.startsWith('geo-denied')) {
      const response = await fetch('https://localhost:5000/get-mac', {
        method: 'GET',
        signal: AbortSignal.timeout(3000), // 3s timeout
      });
      const data = await response.json();
      return await hashString(data.mac_address) + "-" + geo;
    } else {
      throw new Error(`Geolocation error: ${geo}`);
    }
  };

  useEffect(() => {
    isLoading(false);
    setError({ username: '', password: '' });
    setTimeout(() => {
      document.getElementById('username')?.focus();
    }, 100);
  }, [navigate]);

  const validateForm = () => {
    let isValid = true;
    const newError = { username: '', password: '' };

    if (username.trim() === '') {
      newError.username = 'Username cannot be empty.';
      isValid = false;
    } else if (username.length < 3) {
      newError.username = 'Username must be at least 3 characters long.';
      isValid = false;
    } else if (username.trim() !== username) {
      newError.username = 'Username cannot contain spaces.';
      isValid = false;
    }

    if (password.trim() === '') {
      newError.password = 'Password cannot be empty.';
      isValid = false;
    } else if (password.length < 6) {
      newError.password = 'Password must be at least 6 characters long.';
      isValid = false;
    }

    setError(newError);
    return isValid;
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    isLoading(true);

    // Always generate fresh — same hardware = same hash every time
    // This also handles the case where localStorage was cleared
    getDeviceKey().then((devicehash) => {
      console.log(devicehash);

      // Update localStorage with fresh hash
      localStorage.setItem('deviceID', devicehash);
      // setCurrentDeviceHash(devicehash); 

      axios.post(baseURL + 'auth/login', {
        username: username.toLowerCase().trim(),
        password: password,
        devicehash: devicehash    // ← fresh from hardware, not localStorage
      })
        .then((response) => {
          const data = response.data;

          sessionStorage.setItem('token', data.token);
          sessionStorage.setItem('username', username.toLowerCase());
          sessionStorage.setItem('name', data.name);
          sessionStorage.setItem('group_id', data.group_id);
          sessionStorage.setItem('password_changed', data.password_changed);
          sessionStorage.setItem('email', data.email);
          sessionStorage.setItem('isLoggedIn', true);

          if (data.is_coradmin) {
            sessionStorage.setItem('isAdmin', true);
          }

          isLoading(false);

          if (data.first_login === true) {
            setShowDeviceLockInfo(true);
          } else {
            navigate('/dashboard');
          }
        })
        .catch((err) => {
          console.log(err)
          isLoading(false);
          const status = err?.response?.status;
          const msg = err?.response?.data?.error;

          if (status === 404) {
            setError({ username: msg, password: '' });
            setLocationModal(false);
          } else if (status === 401) {
            setError({ username: '', password: msg });
            setLocationModal(false);
          } else if (status === 403) {
            setError({ username: '', password: msg });
            setLocationModal(false);
          } else {
            console.log(err)
            setError({ username: '', password: 'Something went wrong. Try again.' });
            setLocationModal(false);
          }
        });
    },
      (error) => {
        isLoading(false);
        setLocationModal(true);
      });

  };

  const togglePasswordVisibility = () => setShowPassword((v) => !v);

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  return (
    <>

      {showDeviceLockInfo && ReactDOM.createPortal(
        <div className="device-lock-overlay">
          <div className="device-lock-modal">
            <div className="device-lock-icon">🔐</div>
            <h3>This Device is Now Registered</h3>
            <p className="device-lock-subtitle">
              Welcome, <strong>{sessionStorage.getItem('name')}</strong>!
              Your device has been successfully linked to your account.
            </p>
            <div className="device-lock-info-list">
              <div className="device-lock-info-item">
                <span className="info-icon">✅</span>
                <span>This is the <strong>only device</strong> you can use to log in.</span>
              </div>
              <div className="device-lock-info-item">
                <span className="info-icon">🚫</span>
                <span>Logging in from any <strong>other device or browser</strong> will be blocked.</span>
              </div>
              <div className="device-lock-info-item">
                <span className="info-icon">💻</span>
                <span>Always use this <strong>installed app</strong> on this device to log in.</span>
              </div>
              <div className="device-lock-info-item">
                <span className="info-icon">🔄</span>
                <span>If you change your device, contact your <strong>Admin</strong> to reset your device binding.</span>
              </div>
            </div>
            <button
              className="device-lock-btn"
              onClick={() => {
                setShowDeviceLockInfo(false);
                navigate('/dashboard');
              }}
            >
              I Understand, Continue →
            </button>
          </div>
        </div>,
        document.body   // ← renders directly into body, outside all modals
      )}

      {/* ── Login Form ── */}
      <div className="login-form-container" id="container">
        <h2>Sign In</h2>

        <form onSubmit={handleLogin} noValidate>
          {/* ── Username ── */}
          <label htmlFor="username">Employee ID</label>
          <div className="input-group">
            <FaUserAlt className="input-icon" aria-hidden="true" />
            <input
              type="text"
              id="username"
              value={username}
              autoComplete="username"
              placeholder="Enter your Employee ID"
              onChange={(e) => {
                setUsername(e.target.value);
                setError((prev) => ({ ...prev, username: '' }));
              }}
            />
          </div>
          {error.username && (
            <div className="error-message" role="alert">{error.username}</div>
          )}

          {/* ── Password ── */}
          <label htmlFor="password">Password</label>
          <div className="input-group">
            <FaLock className="input-icon" aria-hidden="true" />
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              value={password}
              autoComplete="current-password"
              placeholder="Enter your password"
              onChange={(e) => {
                setPassword(e.target.value);
                setError((prev) => ({ ...prev, password: '' }));
              }}
            />
            <span
              className="eye-icon"
              onClick={togglePasswordVisibility}
              role="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <FaEye /> : <FaEyeSlash />}
            </span>
          </div>
          {error.password && (
            <div className="error-message" role="alert">{error.password}</div>
          )}

          {/* ── Submit / Loader ── */}
          <div className="form-buttons">
            {loading ? (
              <GridLoader color="#2C3493" loading={true} size={14} />
            ) : (
              <button type="submit">Login</button>
            )}
          </div>

          {/* ── Forgot password ── */}
          <div
            className="forgot-password"
            onClick={handleForgotPassword}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleForgotPassword()}
          >
            Forgot Password?
          </div>
        </form>
      </div>

      {locationModal && (
        <div className="location-modal">
          <div className="modal-content">
            <h3>Location Access Required</h3>
            <p>Please allow location access to Login.</p>
            <button className="close-button" onClick={() => setLocationModal(false)}>Close</button>
          </div>
        </div>
      )}
    </>
  );
}
export default LoginForm;